import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { Partition } from '@classes/simulation/partition';
import { KpiByYear } from '@classes/simulation/kpi-by-year';
import { SimulationFlagType } from '@enums/simulation-flag-type.enum';
import { IndiceModeType } from '@enums/indice-mode-type.enum';
import { SimulationService } from '@services/simulation.service';
import { PartitionType } from '@enums/partition-type.enum';

interface PartitionRow {
    partition: Partition;
    deep: number;
    isOpen: boolean;
    isLast: boolean;
    isHidden: boolean;
}

@Component({
    selector: 'app-datatable-by-partitions',
    templateUrl: './datatable-by-partitions.component.html',
    styleUrls: [ './datatable-by-partitions.component.scss' ]
})
export class DatatableByPartitionsComponent implements OnInit, OnDestroy {
    private simuSub: Subscription;

    @Input() partitions: Partition[];
    @Input() kpisByYear: KpiByYear[];

    @Input() isParamMode: boolean;
    @Input() showCompletionRate: boolean;
    @Input() isMJP: boolean;

    partsRows: PartitionRow[];
    columns: { label: string; isTranslated?: boolean }[];
    flagType = SimulationFlagType;
    indiceModeType = IndiceModeType;

    constructor(private simuSrv: SimulationService) {}

    ngOnInit() {
        if (this.partitions) {
            this.setTable();
        }

        this.initSubscriptions();
    }

    onToggleChildren(selectedRow: PartitionRow): void {
        if (selectedRow.isLast) {
            // nothing to do
            return;
        }

        let doesChange = false;

        this.partsRows.map(row => {
            if (row === selectedRow) {
                // toggle status
                row.isOpen = !row.isOpen;
                doesChange = true;
                // avoid opening rows of other categories
            } else if (row.deep <= selectedRow.deep) {
                doesChange = false;
            }

            if (doesChange && row.deep > selectedRow.deep) {
                if (selectedRow.isOpen) {
                    // show only next rows
                    if (row.deep === selectedRow.deep + 1) {
                        row.isHidden = false;
                    }
                } else {
                    // close all deeper rows
                    if (row.deep > selectedRow.deep) {
                        row.isHidden = true;
                        row.isOpen = false;
                    }
                }
            }
        });
    }

    setRowPadding(deep: number): string {
        const padding = 10 + (deep - 1) * 20;
        return padding + 'px';
    }

    private setTable(): void {
        this.yearsFromPartitions();
        this.rowsFromPartitions();
    }

    private yearsFromPartitions(): void {
        this.columns = this.partitions[0].simulationValues.years.map(year => {
            return { label: year.year.toString() };
        });

        if (this.isParamMode) {
            this.columns = this.isMJP
                ? []
                : [
                      { label: 'dashboard.datatables.parameters.completionRate.shortLabel', isTranslated: true },
                      { label: 'dashboard.datatables.parameters.projectionRate.shortLabel', isTranslated: true }
                  ];

            this.partitions[0].simulationValues.years.map(year => {
                if (year.isEstimated) {
                    this.columns.push({ label: year.year.toString() });
                }
            });
        }
    }

    private rowsFromPartitions(): void {
        this.partsRows = this.partitions
            .filter(p => p.isVisible)
            .filter(
                p =>
                    this.isParamMode
                        ? p.simulationValues.flag !== SimulationFlagType.virtual ||
                          p.partitionDetails.partitionType === PartitionType.funding ||
                          p.partitionDetails.partitionType === PartitionType.invest ||
                          p.partitionDetails.partitionType === PartitionType.simulationFile
                        : true
            )
            .reduce((accumulator, partition, i, fParts) => {
                const deepValue = partition.partitionDetails.level;

                return [
                    ...accumulator,
                    {
                        partition: partition,
                        deep: deepValue,
                        isLast: !fParts[i + 1] || fParts[i + 1].parentCode !== partition.partitionDetails.code,
                        isOpen: deepValue > 1 ? false : true,
                        isHidden: deepValue > 2 ? true : false // only show levels 0 and 1
                    }
                ];
            }, []);
    }

    private initSubscriptions(): void {
        this.simuSub = this.simuSrv.selectedSimulationChanged.subscribe(simuFromSrv => {
            if (simuFromSrv) {
                this.partitions = simuFromSrv.partitions;
                this.kpisByYear = simuFromSrv.kpisByYear;
                this.setTable();
            }
        });
    }

    ngOnDestroy(): void {
        if (this.simuSub) {
            this.simuSub.unsubscribe();
        }
    }
}
